import { useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import ScannerPerson from './assets/scanner-person'
import ScannerLine from './assets/scanner-line'
import {
  buildPhaseLoop,
  AnimationComponentProps as Props,
} from './phaseAnimation'

const REFERENCE_SIZE = 24

const LINE_TOP = 0
const LINE_CENTER = 12
const LINE_BOTTOM = 23

const LINE_Y = [LINE_CENTER, LINE_BOTTOM, LINE_CENTER, LINE_TOP]
const LINE_EASINGS = [
  Easing.linear,
  Easing.linear,
  Easing.linear,
  Easing.linear,
]

const PERSON_OPACITY = [0.25, 1, 0.25, 1]
const PERSON_EASINGS = [
  Easing.out(Easing.ease),
  Easing.in(Easing.ease),
  Easing.out(Easing.ease),
  Easing.in(Easing.ease),
]

const PHASE_DURATIONS = [1000, 1000, 1000, 1000]
const PHASE_DELAYS = [0, 0, 0, 0]

export default function PersonScanning({
  size = REFERENCE_SIZE,
  color = colors.white,
  durations = PHASE_DURATIONS,
  delays = PHASE_DELAYS,
  isActive = true,
}: Props) {
  const scale = size / REFERENCE_SIZE

  const lineY = useSharedValue(LINE_TOP * scale)
  const personOpacity = useSharedValue(1)

  useEffect(() => {
    if (!isActive) {
      cancelAnimation(lineY)
      cancelAnimation(personOpacity)
      return
    }

    lineY.set(
      buildPhaseLoop(
        LINE_Y.map((value) => value * scale),
        durations,
        delays,
        LINE_EASINGS
      )
    )

    personOpacity.set(
      buildPhaseLoop(PERSON_OPACITY, durations, delays, PERSON_EASINGS)
    )
  }, [isActive, scale, durations, delays, lineY, personOpacity])

  const lineStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: lineY.value }],
  }))

  const personStyle = useAnimatedStyle(() => ({
    opacity: personOpacity.value,
  }))

  return (
    <View
      style={{ width: size, height: size }}
      className="items-center justify-center"
    >
      {/* Absolute wrappers need explicit dimensions on native: without
          them the view collapses to 0x0 and the SVG at 100% renders nothing. */}
      <Animated.View
        className="absolute"
        style={[{ width: size, height: size }, personStyle]}
      >
        <ScannerPerson size="100%" color={color} />
      </Animated.View>
      <Animated.View
        className="absolute"
        style={[{ width: size, height: size }, lineStyle]}
      >
        <ScannerLine size="100%" color={color} />
      </Animated.View>
    </View>
  )
}
